import React, { useMemo, useEffect, useRef } from 'react'
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api'
import type { Libraries } from '@googlemaps/js-api-loader'
import { GOOGLE_MAP_API_KEY } from '../../constants/Constants'
import { getFormattedDate } from '../../helpers/timeHelper'
import incrementGoogleMapsLoadCount from '../../utils/googleMapsLoadCounter'
import './QrTagEventsMap.css'

type Props = {
  events: IQrTagEvent[]
  selectedEventId: number | null
  onSelectEvent: (eventId: number | null) => void
  googleMapsApiKey?: string
}

const mapLibraries: Libraries = []

type InnerProps = Required<Pick<Props, 'googleMapsApiKey'>> & Omit<Props, 'googleMapsApiKey'>

const QrTagEventsMapInner = ({
  events,
  selectedEventId,
  onSelectEvent,
  googleMapsApiKey,
}: InnerProps) => {
  const {
    isLoaded,
    loadError,
  } = useJsApiLoader({
    googleMapsApiKey,
    libraries: mapLibraries,
  })
  const hasCountedLoadRef = useRef(false)

  const eventsWithLocation = useMemo(() => events
    .filter((event): event is IQrTagEvent & { latitude: number; longitude: number } => (
      event.latitude != null && event.longitude != null
    ))
    .map((event) => ({
      ...event,
      latitude: Number(event.latitude),
      longitude: Number(event.longitude),
    })), [events])

  const activeEvent = useMemo(() => {
    if (selectedEventId == null) {
      return eventsWithLocation[0]
    }
    return eventsWithLocation.find((event) => event.id === selectedEventId) || eventsWithLocation[0]
  }, [eventsWithLocation, selectedEventId])

  useEffect(() => {
    if (!isLoaded || hasCountedLoadRef.current) {
      return
    }
    incrementGoogleMapsLoadCount('QrTagEventsMap')
    hasCountedLoadRef.current = true
  }, [isLoaded])

  if (loadError) {
    return (
      <div className="qr-tag-map" data-testid="qr-tag-events-map">
        <div className="qr-tag-map__error">地図を読み込めませんでした。</div>
      </div>
    )
  }

  if (!eventsWithLocation.length) {
    return (
      <div className="qr-tag-map" data-testid="qr-tag-events-map">
        <div className="qr-tag-map__message">位置情報を含むイベントはまだありません。</div>
      </div>
    )
  }

  if (!isLoaded || !activeEvent) {
    return <div className="qr-tag-map" data-testid="qr-tag-events-map" />
  }

  return (
    <div className="qr-tag-map" data-testid="qr-tag-events-map">
      <GoogleMap
        mapContainerClassName="qr-tag-map__canvas"
        center={{
          lat: activeEvent.latitude,
          lng: activeEvent.longitude,
        }}
        zoom={16}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }}
      >
        {eventsWithLocation.map((event) => (
          <Marker
            key={event.id}
            position={{ lat: event.latitude, lng: event.longitude }}
            onClick={() => onSelectEvent(event.id)}
          >
            {activeEvent.id === event.id && (
              <InfoWindow
                position={{ lat: event.latitude, lng: event.longitude }}
                onCloseClick={() => onSelectEvent(null)}
              >
                <div className="qr-tag-map__info">
                  <strong>{event.event_type}</strong>
                  <div>{getFormattedDate(event.occurred_at)}</div>
                  <div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Googleマップで開く
                    </a>
                  </div>
                </div>
              </InfoWindow>
            )}
          </Marker>
        ))}
      </GoogleMap>
    </div>
  )
}

const QrTagEventsMap = ({ googleMapsApiKey, ...rest }: Props) => {
  const effectiveApiKey = googleMapsApiKey ?? GOOGLE_MAP_API_KEY
  if (!effectiveApiKey) {
    return (
      <div className="qr-tag-map" data-testid="qr-tag-events-map">
        <div className="qr-tag-map__message">地図を表示するための設定が完了していません。</div>
      </div>
    )
  }

  return (
    <QrTagEventsMapInner
      {...rest}
      googleMapsApiKey={effectiveApiKey}
    />
  )
}

export default QrTagEventsMap
